import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { doc, onSnapshot, getDoc, updateDoc, runTransaction, increment } from 'firebase/firestore';
import { db } from '../App';
import { useFireAuth } from '../FireAuthContext';
import useBank from './useBank';
import { createDeck, shuffleDeck, evaluateHand, dealCommunityCards } from './PokerFunctions';
import { BET_DENOMINATIONS } from '../utils/GameConstants';
import './PokerGame.css';
import './Games.css';

const PokerGame = () => {
  const { gameId } = useParams();
  const { user, updateUser } = useFireAuth();
  const { withdrawMoney, refreshUser } = useBank();
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [betAmount, setBetAmount] = useState(0);
  const [raising, setRaising] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const betDenominations = BET_DENOMINATIONS;
  const [playerCache, setPlayerCache] = useState({});
  const [playerBets, setPlayerBets] = useState({ player1: 0, player2: 0 });

  useEffect(() => {
    if (!gameId) return;

    const gameDocRef = doc(db, 'games', gameId);
    const unsubscribe = onSnapshot(gameDocRef, (doc) => {
      if (doc.exists()) {
        setGameData(doc.data());
        setLoading(false);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [gameId]);

  const updateUserNamesAndBalances = useCallback(async (gameData) => {
    const player1Doc = playerCache[gameData.player1] || await getDoc(doc(db, 'users', gameData.player1));
    const player2Doc = gameData.player2 ? (playerCache[gameData.player2] || await getDoc(doc(db, 'users', gameData.player2))) : null;

    setPlayerCache((prevCache) => ({
      ...prevCache,
      [gameData.player1]: player1Doc,
      ...(gameData.player2 ? { [gameData.player2]: player2Doc } : {})
    }));

    setGameData((prevData) => ({
      ...prevData,
      player1Name: player1Doc.exists() ? player1Doc.data().name : 'Player 1',
      player2Name: player2Doc && player2Doc.exists() ? player2Doc.data().name : 'Player 2',
      player1Balance: player1Doc.exists() ? player1Doc.data().money : 0,
      player2Balance: player2Doc && player2Doc.exists() ? player2Doc.data().money : 0,
    }));
  }, [playerCache]);

  useEffect(() => {
    if (gameData) {
      const debounceTimeout = setTimeout(() => {
        updateUserNamesAndBalances(gameData);
      }, 300);

      return () => clearTimeout(debounceTimeout);
    }
  }, [gameData, updateUserNamesAndBalances]);

  const dealInitialCards = async () => {
    const deck = createDeck();
    shuffleDeck(deck);
    const player1Cards = [deck.pop(), deck.pop()];
    const player2Cards = [deck.pop(), deck.pop()];

    await updateDoc(doc(db, 'games', gameId), {
      player1Cards: player1Cards,
      player2Cards: player2Cards,
      deck: deck,
      currentPlayer: gameData.player1,
      currentBet: 0,
      pot: 0,
      communityCards: [],
      stage: 'pre-flop',
      lastAction: '',
      winner: null,
      player1Score: null,
      player2Score: null,
      playerBets: { player1: 0, player2: 0 }
    });
    setPlayerBets({ player1: 0, player2: 0 });
    setShowSummary(false);
  };

  const placeBet = async (amount) => {
    const currentPlayerKey = gameData.currentPlayer === gameData.player1 ? 'player1' : 'player2';
    const otherPlayerKey = currentPlayerKey === 'player1' ? 'player2' : 'player1';
    const currentPlayerBet = playerBets[currentPlayerKey] + amount;
    const otherPlayerBet = playerBets[otherPlayerKey];
    const newPot = gameData.pot + amount;
    const newCurrentBet = Math.max(currentPlayerBet, otherPlayerBet);
    const nextPlayer = gameData.currentPlayer === gameData.player1 ? gameData.player2 : gameData.player1;

    await runTransaction(db, async (transaction) => {
      const userDoc = doc(db, 'users', user.uid);
      transaction.update(userDoc, {
        money: increment(-amount)
      });

      const gameDoc = doc(db, 'games', gameId);
      transaction.update(gameDoc, {
        pot: newPot,
        currentBet: newCurrentBet,
        currentPlayer: nextPlayer,
        lastAction: currentPlayerBet > otherPlayerBet ? 'raise' : 'bet',
        [`playerBets.${currentPlayerKey}`]: currentPlayerBet,
      });
    });

    await updateUser();
    setPlayerBets(prevBets => ({
      ...prevBets,
      [currentPlayerKey]: currentPlayerBet
    }));
    setRaising(false);
    await refreshUser();
  };

  const fold = async () => {
    const winner = gameData.currentPlayer === gameData.player1 ? gameData.player2 : gameData.player1;
    await updateDoc(doc(db, 'games', gameId), {
      winner: winner,
      stage: 'end',
      lastAction: 'fold',
    });

    await finalizeGame(winner);
  };

  const finalizeGame = async (winner) => {
    const gameDoc = await getDoc(doc(db, 'games', gameId));
    if (gameDoc.exists()) {
      const data = gameDoc.data();
      if (data) {
        const player1Hand = [...data.player1Cards, ...data.communityCards];
        const player2Hand = [...data.player2Cards, ...data.communityCards];
        const player1Score = evaluateHand(player1Hand);
        const player2Score = evaluateHand(player2Hand);

        let winningPlayer;
        if (!winner) {
          if (player1Score.rank > player2Score.rank) {
            winningPlayer = data.player1;
          } else if (player2Score.rank > player1Score.rank) {
            winningPlayer = data.player2;
          } else if (player1Score.highCard > player2Score.highCard) {
            winningPlayer = data.player1;
          } else if (player2Score.highCard > player1Score.highCard) {
            winningPlayer = data.player2;
          } else {
            winningPlayer = null;
          }
        } else {
          winningPlayer = winner;
        }

        const player1DocRef = doc(db, 'users', data.player1);
        const player2DocRef = doc(db, 'users', data.player2);

        await runTransaction(db, async (transaction) => {
          if (winningPlayer === data.player1) {
            transaction.update(player1DocRef, { money: increment(data.pot) });
          } else if (winningPlayer === data.player2) {
            transaction.update(player2DocRef, { money: increment(data.pot) });
          } else {
            transaction.update(player1DocRef, { money: increment(data.pot / 2) });
            transaction.update(player2DocRef, { money: increment(data.pot / 2) });
          }
        });

        await updateDoc(doc(db, 'games', gameId), {
          player1Cards: [],
          player2Cards: [],
          communityCards: [],
          deck: createDeck(),
          currentPlayer: data.player1,
          currentBet: 0,
          pot: 0,
          stage: 'pre-flop',
          lastAction: '',
        });

        if (winningPlayer) {
          await withdrawMoney(data.pot);
        }

        setGameData((prevData) => ({
          ...prevData,
          winner: winningPlayer,
          player1Score,
          player2Score,
        }));
        setShowSummary(true);
        await refreshUser();
      }
    }
  };

  const callBet = async () => {
    const currentPlayerKey = gameData.currentPlayer === gameData.player1 ? 'player1' : 'player2';
    const otherPlayerKey = currentPlayerKey === 'player1' ? 'player2' : 'player1';
    const amountToCall = playerBets[otherPlayerKey] - playerBets[currentPlayerKey];
    const newPot = gameData.pot + amountToCall;
    const nextPlayer = gameData.currentPlayer === gameData.player1 ? gameData.player2 : gameData.player1;

    await runTransaction(db, async (transaction) => {
      const userDoc = doc(db, 'users', user.uid);
      transaction.update(userDoc, {
        money: increment(-amountToCall)
      });

      const gameDoc = doc(db, 'games', gameId);
      transaction.update(gameDoc, {
        pot: newPot,
        currentBet: 0,
        currentPlayer: nextPlayer,
        lastAction: 'call',
        [`playerBets.${currentPlayerKey}`]: playerBets[otherPlayerKey],
      });
    });

    await updateUser();
    setPlayerBets(prevBets => ({
      ...prevBets,
      [currentPlayerKey]: playerBets[otherPlayerKey]
    }));

    if (playerBets.player1 === playerBets.player2) {
      await moveToNextStage();
    }

    await refreshUser();
  };

  const moveToNextStage = async () => {
    let nextStage = gameData.stage;
    if (gameData.stage === 'pre-flop') {
      nextStage = 'flop';
      await dealCommunityCards(gameId, 3, gameData);
    } else if (gameData.stage === 'flop') {
      nextStage = 'turn';
      await dealCommunityCards(gameId, 1, gameData);
    } else if (gameData.stage === 'turn') {
      nextStage = 'river';
      await dealCommunityCards(gameId, 1, gameData);
    } else if (gameData.stage === 'river') {
      await finalizeGame();
      return;
    }

    await updateDoc(doc(db, 'games', gameId), {
      stage: nextStage,
      playerBets: { player1: 0, player2: 0 },
      currentBet: 0,
      lastAction: '',
    });
    setPlayerBets({ player1: 0, player2: 0 });
  };

  const check = async () => {
    const nextPlayer = gameData.currentPlayer === gameData.player1 ? gameData.player2 : gameData.player1;

    if (gameData.lastAction === 'check') {
      await moveToNextStage();
    } else {
      await updateDoc(doc(db, 'games', gameId), {
        currentPlayer: nextPlayer,
        lastAction: 'check',
      });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!gameData) {
    return <div>Game not found</div>;
  }

  return (
    <div className="poker-game">
      <h2>Poker Game</h2>
      <p>Game ID: {gameId}</p>
      <div className="players">
        <div className="player">
          <h3>Player 1</h3>
          <p>{gameData.player1Name}</p>
          <p>Balance: 💴 {gameData.player1Balance || 0}</p>
          <div className="cards">
            {gameData.player1Cards && gameData.player1Cards.map((card, index) => (
              <span key={index} className="card dark-card">{user.uid === gameData.player1 ? `${card.value}${card.suit}` : '🂠'}</span>
            ))}
          </div>
        </div>
        <div className="player">
          <h3>Player 2</h3>
          <p>{gameData.player2Name || 'Waiting for Player 2...'}</p>
          <p>Balance: 💴 {gameData.player2Balance || 0}</p>
          <div className="cards">
            {gameData.player2Cards && gameData.player2Cards.map((card, index) => (
              <span key={index} className="card dark-card">{user.uid === gameData.player2 ? `${card.value}${card.suit}` : '🂠'}</span>
            ))}
          </div>
        </div>
      </div>
      {showSummary && gameData.winner && (
        <div className="game-summary">
          <h3>Game Summary</h3>
          <p>Winner: {gameData.winner === gameData.player1 ? gameData.player1Name : gameData.player2Name}</p>
          <p>Winning Hand: {gameData.winner === gameData.player1 ? JSON.stringify(gameData.player1Score) : JSON.stringify(gameData.player2Score)}</p>
          <p>Total Pot: 💴 {gameData.pot}</p>
        </div>
      )}
      {gameData.player1 && gameData.player2 && user.uid === gameData.player1 && (!gameData.player1Cards || gameData.player1Cards.length === 0) && (
        <button onClick={dealInitialCards}>Deal Cards</button>
      )}
      {gameData.player1 && gameData.player2 && user.uid === gameData.currentPlayer && !showSummary && (
        <div className="betting">
          <h3>Betting</h3>
          <p>Current Bet: 💴 {gameData.currentBet || 0}</p>
          <p>Pot: 💴 {gameData.pot || 0}</p>
          <p>Your current bet: 💴 {playerBets[gameData.currentPlayer === gameData.player1 ? 'player1' : 'player2']}</p>
          {gameData.currentBet > playerBets[gameData.currentPlayer === gameData.player1 ? 'player1' : 'player2'] ? (
            <div>
              <button onClick={callBet}>Call 💴 {gameData.currentBet - playerBets[gameData.currentPlayer === gameData.player1 ? 'player1' : 'player2']}</button>
              <button onClick={() => setRaising(true)}>Raise</button>
              <button onClick={fold}>Fold</button>
            </div>
          ) : (
            <div>
              <button onClick={check}>Check</button>
              <button onClick={() => setRaising(true)}>Bet</button>
              <button onClick={fold}>Fold</button>
            </div>
          )}
          {raising && (
            <div>
              <div className="bet-buttons">
                {betDenominations.map((amount, index) => (
                  <div
                    key={index}
                    className={`chip chip-${amount}`}
                    onClick={() => setBetAmount(amount)}
                  >
                    <span>{amount}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => placeBet(betAmount)}>Place Bet 💴{betAmount}</button>
            </div>
          )}
        </div>
      )}
      <div className="community-cards">
        <h3>Table Cards</h3>
        <div className="cards">
          {gameData.communityCards && gameData.communityCards.map((card, index) => (
            <span key={index} className="card dark-card">{`${card.value}${card.suit}`}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PokerGame;
